/**
 * Decoder for the streamed arguments of the `cognitive__THINK` tool.
 *
 * The LLM clients forward those arguments to the chat as they arrive (`thinking_text` frames), so the
 * frames are fragments of a JSON object cut at arbitrary points:
 *
 *     {"reasoning":"Check the cohort table.\nThen compare "    +    "the \"two\" quarters."}
 *
 * (ClaudeClient forwards `partial_json`; CopilotLLMClient sends the whole `json.dumps({"reasoning": ...})`.)
 * Shown as received, the reader sees the envelope and the escapes as typed characters: `\n`, `\"`.
 * This decoder turns the stream back into the prose the model wrote: the value of the string, with its JSON
 * escapes resolved, whatever the frame boundaries are (an escape or a \uXXXX may be split across frames).
 *
 * Text that does not start with the envelope (native reasoning tokens) is not JSON and is never decoded.
 * What happens to it is the caller's choice (`plain`):
 *   'pass'  return it unchanged (the SSE reasoning block shows it)
 *   'drop'  return '' (the live activity window leaves it to the `thinking.delta` frames that mirror it)
 *
 * One decoder per turn (`reset()` at the start of the next one). A second THINK call in the same turn starts a
 * new envelope; the decoder follows it. No imports, no React.
 *
 * TWO BYTE-IDENTICAL COPIES, edit both: this file and core/agent_studio/apps/web/lib/thinking-text-decoder.mjs
 * (Studio's docker build context is apps/web only, so it cannot import from core/ui). A jest test
 * (reasoningStream.parity.test.js) fails if they drift, and runs the same cases against both.
 *
 * History: the server stores the raw stream (SLMClient `raw_stream` entries in `cot_steps`, and `cot_full_text` =
 * "[Step N] <text>" joined), so a stored turn can hold the same envelopes. `decodeStoredThinking` /
 * `decodeStoredReasoning` decode those; text that is not an envelope at the start of a stored segment is left alone.
 */

/** `{"reasoning":"` with optional whitespace; the keys the THINK handler reads (SLMClient._handle_think). */
const ENVELOPE = /^\s*\{\s*"(?:reasoning|thought|content)"\s*:\s*"/;

/** An opening that has not matched after this many characters is not a THINK envelope. */
const MAX_OPENING = 80;

const SIMPLE_ESCAPES = { n: '\n', t: '\t', r: '', b: '', f: '', '"': '"', '\\': '\\', '/': '/' };

/**
 * @param {{plain?: 'pass'|'drop'}} [options]
 * @returns {{push: (chunk: string) => string, flush: () => string, reset: () => void}}
 */
export function createThinkingTextDecoder({ plain = 'pass' } = {}) {
    // detect: deciding what the stream is; plain: not an envelope; content: inside the string value;
    // done: after the closing quote (waiting for a possible next envelope).
    let mode;
    let head;       // text held back while detecting
    let escaping;   // saw a backslash, waiting for the escape character
    let unicode;    // hex digits of a \uXXXX collected so far, or null

    function reset() {
        mode = 'detect';
        head = '';
        escaping = false;
        unicode = null;
    }
    reset();

    const passPlain = (text) => (plain === 'pass' ? text : '');

    /** Decode inside the string value. Returns the prose and how much of `text` it used (through the closing quote). */
    function content(text) {
        let out = '';
        let used = 0;
        for (const ch of text) {
            used += ch.length;
            if (unicode !== null) {
                unicode += ch;
                if (unicode.length === 4) {
                    out += /^[0-9a-fA-F]{4}$/.test(unicode) ? String.fromCharCode(parseInt(unicode, 16)) : '';
                    unicode = null;
                }
            } else if (escaping) {
                escaping = false;
                if (ch === 'u') unicode = '';
                else out += Object.prototype.hasOwnProperty.call(SIMPLE_ESCAPES, ch) ? SIMPLE_ESCAPES[ch] : ch;
            } else if (ch === '\\') {
                escaping = true;
            } else if (ch === '"') {
                mode = 'done';
                break;
            } else {
                out += ch;
            }
        }
        return { out, used };
    }

    function push(chunk) {
        if (typeof chunk !== 'string' || chunk === '') return '';
        let text = chunk;
        let out = '';
        // A chunk can finish one envelope and start the next, so loop until it is used up.
        while (text !== '') {
            if (mode === 'plain') return out + passPlain(text);

            if (mode === 'detect') {
                head += text;
                text = '';
                const trimmed = head.trimStart();
                if (trimmed === '') return out;               // only whitespace so far
                if (trimmed[0] !== '{') {                     // not an envelope: plain reasoning text
                    const all = head;
                    head = '';
                    mode = 'plain';
                    return out + passPlain(all);
                }
                const m = ENVELOPE.exec(head);
                if (m) {
                    const after = head.slice(m[0].length);
                    head = '';
                    mode = 'content';
                    text = after;
                    continue;
                }
                if (head.length >= MAX_OPENING) {             // starts with "{" but is not the THINK envelope
                    const all = head;
                    head = '';
                    mode = 'plain';
                    return out + passPlain(all);
                }
                return out;                                   // keep waiting for the rest of the opening
            }

            if (mode === 'content') {
                // Decode up to the closing quote; what follows it belongs to the next envelope, if any.
                const { out: piece, used } = content(text);
                out += piece;
                text = text.slice(used);
                continue;
            }

            // done: skip the tail of the object; a "{" begins another THINK call.
            const next = text.indexOf('{');
            if (next === -1) return out;
            mode = 'detect';
            head = '';
            text = text.slice(next);
        }
        return out;
    }

    /** End of the stream: an opening still being detected is shown as plain text rather than lost. */
    function flush() {
        if (mode !== 'detect' || head === '') return '';
        const all = head;
        head = '';
        mode = 'plain';
        return passPlain(all);
    }

    return { push, flush, reset };
}

/** Where an envelope may start inside stored text: the start, a blank line, or a "[Step N] " prefix. */
const STORED_START = /(^|\n\n|\[Step \d+\] )(\s*\{\s*"(?:reasoning|thought|content)"\s*:\s*")/g;
/** A further envelope right after one that just ended (several THINK calls stored back to back). */
const STORED_NEXT = /\s*\{\s*"(?:reasoning|thought|content)"\s*:\s*"/y;

/** Decode the JSON string body that starts at `from`; returns the prose and the index after the closing quote. */
function decodeStringBody(text, from) {
    let out = '';
    let i = from;
    while (i < text.length) {
        const ch = text[i++];
        if (ch === '"') return { out, end: i };
        if (ch !== '\\') { out += ch; continue; }
        if (i >= text.length) break;                          // cut off right after a backslash
        const esc = text[i++];
        if (esc === 'u') {
            const hex = text.slice(i, i + 4);
            i += hex.length;
            if (/^[0-9a-fA-F]{4}$/.test(hex)) out += String.fromCharCode(parseInt(hex, 16));
        } else {
            out += Object.prototype.hasOwnProperty.call(SIMPLE_ESCAPES, esc) ? SIMPLE_ESCAPES[esc] : esc;
        }
    }
    return { out, end: text.length };                         // no closing quote: stored text was cut short
}

/**
 * Decode THINK envelopes in a stored (complete) string. Only envelopes that begin the text, a paragraph or a
 * "[Step N] " segment are decoded; the closing `}` is dropped. Everything else is returned untouched, and a
 * non-string comes back as it is.
 * @param {any} text
 * @returns {any}
 */
export function decodeStoredThinking(text) {
    if (typeof text !== 'string' || text === '') return text;
    let out = '';
    let pos = 0;
    STORED_START.lastIndex = 0;
    let m;
    while ((m = STORED_START.exec(text)) !== null) {
        out += text.slice(pos, m.index) + m[1];
        let { out: prose, end } = decodeStringBody(text, m.index + m[0].length);
        out += prose;
        for (;;) {
            const tail = /^\s*\}/.exec(text.slice(end));
            if (tail) end += tail[0].length;
            STORED_NEXT.lastIndex = end;
            const next = STORED_NEXT.exec(text);
            if (!next) break;
            const more = decodeStringBody(text, end + next[0].length);
            out += more.out;
            end = more.end;
        }
        pos = end;
        STORED_START.lastIndex = end;
    }
    return pos === 0 && out === '' ? text : out + text.slice(pos);
}

const decodeStep = (step) => {
    if (typeof step === 'string') return decodeStoredThinking(step);
    if (!step || typeof step !== 'object') return step;
    let out = step;
    for (const key of ['text', 'content']) {
        const v = decodeStoredThinking(step[key]);
        if (v !== step[key]) out = out === step ? { ...step, [key]: v } : { ...out, [key]: v };
    }
    return out;
};

function decodeCot(holder) {
    let out = holder;
    if (Array.isArray(holder.cot_steps)) {
        const steps = holder.cot_steps.map(decodeStep);
        if (steps.some((s, i) => s !== holder.cot_steps[i])) out = { ...out, cot_steps: steps };
    }
    const full = decodeStoredThinking(holder.cot_full_text);
    if (full !== holder.cot_full_text) out = { ...out, cot_full_text: full };
    return out;
}

/**
 * A history message with its stored reasoning (`cot_steps`, `cot_full_text`, also under `metadata`) decoded.
 * Returns the same object when there is nothing to decode; never mutates its input.
 * @param {any} message
 * @returns {any}
 */
export function decodeStoredReasoning(message) {
    if (!message || typeof message !== 'object') return message;
    let out = decodeCot(message);
    if (message.metadata && typeof message.metadata === 'object') {
        const meta = decodeCot(message.metadata);
        if (meta !== message.metadata) out = { ...out, metadata: meta };
    }
    return out;
}
